const PREFIX = '@OPP:';
const LOGIN_PATH = '/auth';

function isStorageQuotaError(error: unknown): boolean {
  if (!(error instanceof DOMException)) return false;

  return (
    error.name === 'QuotaExceededError' ||
    error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    // Códigos legados (Chrome / Firefox)
    error.code === 22 ||
    error.code === 1014
  );
}

function clearProjectStorage(): void {
  Object.keys(localStorage)
    .filter((key) => key.startsWith(PREFIX))
    .forEach((key) => localStorage.removeItem(key));
}

/**
 * Limpa o storage do projeto (@OPP:) e redireciona para o login.
 */
export function forceLogoutToLogin(reason = 'STORAGE_RESET'): never {
  try {
    clearProjectStorage();
  } catch {
    // ignore — objetivo é liberar espaço e sair da sessão
  }
  window.location.href = LOGIN_PATH;
  throw new Error(reason);
}

function recoverFromStorageError(error: unknown): never {
  if (isStorageQuotaError(error)) {
    forceLogoutToLogin('STORAGE_QUOTA_EXCEEDED');
  }

  throw error;
}

/**
 * Serviço para manipulação do localStorage com prefixo @OPP:
 */
export const storage = {
  /**
   * Recupera um valor do storage
   * @param key - Chave do storage
   * @returns Valor do storage ou null se não existir
   * @throws Error se o valor não puder ser convertido de JSON
   */
  get<T>(key: string): T | null {
    if (!key) throw new Error('STORAGE_KEY_REQUIRED');

    const data = localStorage.getItem(PREFIX + key);
    if (!data) return null;

    try {
      return data.startsWith('{') || data.startsWith('[')
        ? JSON.parse(data) as T
        : data as T;
    } catch (error) {
      console.log(error);
      throw new Error(`INVALID_STORAGE_VALUE`);
    }
  },

  /**
   * Salva um valor no storage
   * @param key - Chave do storage
   * @param value - Valor a ser salvo
   * @throws Error se a chave for vazia
   */
  set<T>(key: string, value: T): void {
    if (!key) throw new Error('STORAGE_KEY_REQUIRED');

    try {
      const data = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(PREFIX + key, data);
    } catch (error) {
      recoverFromStorageError(error);
    }
  },

  /**
   * Remove um valor do storage
   * @param key - Chave do storage
   * @throws Error se a chave for vazia
   */
  remove(key: string): void {
    if (!key) throw new Error('STORAGE_KEY_REQUIRED');
    localStorage.removeItem(PREFIX + key);
  },

  /**
   * Remove todos os valores do storage com prefixo @OPP:
   */
  clear(): void {
    clearProjectStorage();
  },
};
